import { keepPreviousData, useQuery } from '@tanstack/react-query'
import { LayoutGrid, Rows3, Search, SlidersHorizontal } from 'lucide-react'
import { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { EMPTY_FILTERS, FilterPanel, activeFilterCount, type Facets, type FilterState } from './FilterPanel'
import { RecordCard, type BrowseRecord } from './RecordCard'
import { RecordDetailModal } from './RecordDetailModal'
import { Pagination } from '../admin/MasterDataTable'
import { Badge, Swatch } from '@/components/ui/Badge'
import { SectionTabs } from '@/components/SectionTabs'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Field'
import { Select } from '@/components/ui/Select'
import { Card, EmptyState, LoadingPanel } from '@/components/ui/misc'
import { useComparisonList } from '@/hooks/useComparisonList'
import { useDebounced } from '@/hooks/useDebounced'
import { api, queryString } from '@/lib/api'
import { cn, emptyDash, formatDate } from '@/lib/utils'

const PAGE_SIZE = 24

const SORTS = [
  { value: 'recent', label: 'Most recent' },
  { value: 'oldest', label: 'Oldest first' },
  { value: 'title', label: 'Title A–Z' },
  { value: 'lightness', label: 'Lightness (L*)' },
  { value: 'hue', label: 'Hue' },
]

export function PublicDatabase() {
  const navigate = useNavigate()
  const [search, setSearch] = useState('')
  const [filters, setFilters] = useState<FilterState>(EMPTY_FILTERS)
  const [sort, setSort] = useState('recent')
  const [page, setPage] = useState(1)
  const [layout, setLayout] = useState<'grid' | 'table'>('grid')
  const [openRecord, setOpenRecord] = useState<string | null>(null)
  const { ids: compare, toggle: toggleCompare, clear: clearCompare } = useComparisonList()

  const debouncedSearch = useDebounced(search, 300)

  // Both the grid and the facet counts read the same filter state, so a count
  // always describes the result set a click would produce.
  const params = useMemo(
    () => ({
      q: debouncedSearch,
      kind: filters.kind ?? undefined,
      dyeSource: filters.dyeSource.join(','),
      fiber: filters.fiber.join(','),
      mordant: filters.mordant.join(','),
      manuscript: filters.manuscript.join(','),
      institution: filters.institution.join(','),
      weave: filters.weave.join(','),
    }),
    [debouncedSearch, filters],
  )

  const { data, isLoading, isPlaceholderData } = useQuery({
    queryKey: ['browse', params, sort, page],
    queryFn: () =>
      api.get<{ records: BrowseRecord[]; total: number }>(
        `/browse${queryString({ ...params, sort, page, pageSize: PAGE_SIZE })}`,
      ),
    placeholderData: keepPreviousData,
  })

  const { data: facets } = useQuery({
    queryKey: ['browse', 'facets', params],
    queryFn: () => api.get<Facets>(`/browse/facets${queryString(params)}`),
    placeholderData: keepPreviousData,
  })

  const records = data?.records ?? []
  const total = data?.total ?? 0

  return (
    <>
      <SectionTabs />

      <header className="mb-5 flex items-end justify-between gap-4">
        <div>
          <h1 className="font-display text-[28px] font-semibold leading-tight text-ink">
            Public Database
          </h1>
          <p className="mt-1 text-sm text-ink-muted">
            Peer-reviewed dye experiments and historical references from the research community.
          </p>
        </div>
        {compare.length >= 2 && (
          <Button onClick={() => navigate(`/compare?records=${compare.join(',')}`)}>
            Compare {compare.length} records
          </Button>
        )}
      </header>

      <div className="flex gap-7">
        <FilterPanel
          facets={facets}
          filters={filters}
          onChange={(next) => {
            setFilters(next)
            setPage(1)
          }}
        />

        <section className="min-w-0 flex-1">
          <div className="mb-4 flex items-center gap-3">
            <div className="flex-1">
              <Input
                value={search}
                onChange={(event) => {
                  setSearch(event.target.value)
                  setPage(1)
                }}
                placeholder="Search titles, record numbers, researchers and notes..."
                icon={<Search className="size-4" />}
              />
            </div>
            <Select value={sort} onChange={setSort} options={SORTS} className="w-44" />
            <div className="flex rounded-[var(--radius-control)] border border-line p-0.5">
              {([
                ['grid', <LayoutGrid className="size-4" key="g" />],
                ['table', <Rows3 className="size-4" key="t" />],
              ] as const).map(([value, icon]) => (
                <button
                  key={value}
                  type="button"
                  onClick={() => setLayout(value)}
                  className={cn(
                    'rounded-[6px] px-2.5 py-1.5 transition-colors',
                    layout === value ? 'bg-brand-soft text-brand' : 'text-ink-faint hover:text-ink',
                  )}
                  aria-label={value === 'grid' ? 'Grid view' : 'Table view'}
                >
                  {icon}
                </button>
              ))}
            </div>
          </div>

          <div className="mb-3 flex items-center justify-between text-[13px] text-ink-muted">
            <span className="flex items-center gap-2">
              {isLoading ? 'Searching…' : `${total} record${total === 1 ? '' : 's'}`}
              {activeFilterCount(filters) > 0 && (
                <Badge tone="brand" icon={<SlidersHorizontal className="size-3" />}>
                  {activeFilterCount(filters)} filter
                  {activeFilterCount(filters) === 1 ? '' : 's'}
                </Badge>
              )}
            </span>
            {compare.length > 0 && (
              <button
                type="button"
                onClick={clearCompare}
                className="font-semibold text-brand hover:underline"
              >
                Clear {compare.length} selected
              </button>
            )}
          </div>

          {isLoading ? (
            <LoadingPanel label="Loading records" />
          ) : records.length === 0 ? (
            <EmptyState
              title="No records match"
              description="Try removing a filter or broadening your search."
              action={
                <Button
                  variant="secondary"
                  onClick={() => {
                    setFilters(EMPTY_FILTERS)
                    setSearch('')
                  }}
                >
                  Clear filters
                </Button>
              }
            />
          ) : layout === 'grid' ? (
            <div
              className={cn(
                'grid grid-cols-2 gap-4 lg:grid-cols-3 xl:grid-cols-4',
                isPlaceholderData && 'opacity-60',
              )}
            >
              {records.map((record) => (
                <RecordCard
                  key={record.id}
                  record={record}
                  selected={compare.includes(record.id)}
                  onToggleSelect={() => toggleCompare(record.id)}
                  onOpen={() => setOpenRecord(record.id)}
                />
              ))}
            </div>
          ) : (
            <RecordTable records={records} onOpen={setOpenRecord} />
          )}

          {total > PAGE_SIZE && (
            <div className="mt-5 flex items-center justify-between text-[12px] text-ink-muted">
              <Pagination
                page={page}
                totalPages={Math.ceil(total / PAGE_SIZE)}
                onChange={(next) => {
                  setPage(next)
                  window.scrollTo({ top: 0, behavior: 'smooth' })
                }}
              />
              <span>
                Showing {(page - 1) * PAGE_SIZE + 1}–{(page - 1) * PAGE_SIZE + records.length} of{' '}
                {total}
              </span>
            </div>
          )}
        </section>
      </div>

      <RecordDetailModal
        recordId={openRecord}
        onOpenChange={(open) => !open && setOpenRecord(null)}
        onOpenRecord={setOpenRecord}
      />
    </>
  )
}

function RecordTable({
  records,
  onOpen,
}: {
  records: BrowseRecord[]
  onOpen: (id: string) => void
}) {
  return (
    <Card className="overflow-hidden">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-line bg-paper-muted/60 text-left text-[12px] text-ink-muted">
            <th className="px-4 py-2.5 font-semibold">Record</th>
            <th className="px-4 py-2.5 font-semibold">Dye source</th>
            <th className="px-4 py-2.5 font-semibold">Fiber</th>
            <th className="px-4 py-2.5 font-semibold">L* a* b*</th>
            <th className="px-4 py-2.5 font-semibold">Researcher</th>
            <th className="px-4 py-2.5 font-semibold">Published</th>
          </tr>
        </thead>
        <tbody>
          {records.map((record) => (
            <tr
              key={record.id}
              onClick={() => onOpen(record.id)}
              className="cursor-pointer border-b border-line last:border-0 hover:bg-paper-muted/60"
            >
              <td className="px-4 py-2.5">
                <span className="flex items-center gap-2.5">
                  <Swatch hex={record.swatchHex} size="sm" />
                  <span className="min-w-0">
                    <span className="block truncate font-medium text-ink">
                      {record.title || 'Untitled'}
                    </span>
                    <span className="block text-[11px] text-ink-faint">{record.code}</span>
                  </span>
                </span>
              </td>
              <td className="px-4 py-2.5 text-ink-muted">{emptyDash(record.dyeSourceName)}</td>
              <td className="px-4 py-2.5 text-ink-muted">{emptyDash(record.fiberName)}</td>
              <td className="px-4 py-2.5 font-mono text-[12px] text-ink-muted">
                {record.resLabL === null
                  ? '—'
                  : `${record.resLabL} ${record.resLabA} ${record.resLabB}`}
              </td>
              <td className="px-4 py-2.5 text-ink-muted">{emptyDash(record.researcherName)}</td>
              <td className="px-4 py-2.5 text-ink-muted">{formatDate(record.publishedAt)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </Card>
  )
}
